import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Building, Loader2, MoreVertical, Trash2, X, AlertTriangle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getSupplyExecutiveDashboard, deleteSupplier } from '../api';

const numFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US'));
const pctFmt = (n) => (n == null ? 'غير متوفر' : `${Number(n).toFixed(1)}%`);
const moneyFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));

export default function SuppliersListPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialSearch = params.get('name') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch);
  // Which supplier card has its ⋮ menu open (by supplier name).
  const [menuFor, setMenuFor] = useState(null);
  // Supplier queued for deletion — surfaces the confirmation modal.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [flash, setFlash] = useState(null); // {ok, text}

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getSupplyExecutiveDashboard()); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Global click closes the ⋮ menu.
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuFor]);

  const filtered = useMemo(() => {
    const list = data?.supplierPerformance || [];
    const s = search.trim();
    if (!s) return list;
    return list.filter((x) => (x.name || '').toLowerCase().includes(s.toLowerCase()));
  }, [data, search]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await deleteSupplier(pendingDelete.name);
      setFlash({ ok: true, text: `تم حذف بيانات «${pendingDelete.name}» (${Number(res?.deleted || 0).toLocaleString('en-US')} سجل).` });
      setPendingDelete(null);
      await load(); // recompute all KPIs + charts + cards through the existing endpoint
    } catch (e) {
      setFlash({ ok: false, text: e?.response?.data?.detail || e?.message || 'تعذر حذف المورد' });
    } finally { setDeleting(false); }
  }, [pendingDelete, load]);

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="suppliers-list">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[11px] font-semibold" style={{ color: 'var(--se-emerald)' }}>SUPPLIERS</div>
            <h1 className="se-title mt-1">الموردون</h1>
            <div className="se-subtitle">قائمة كاملة بجميع الموردين مع مقاييس أدائهم</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="se-input w-64 pr-8" placeholder="بحث بالاسم"
                value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="supplier-search" />
            </div>
          </div>
        </div>

        {flash && (
          <div className="se-panel p-3 mb-4 flex items-center gap-2" data-testid="supplier-flash"
               style={{ background: flash.ok ? 'rgba(52,211,153,0.12)' : 'rgba(239,68,68,0.12)',
                        border: `1px solid ${flash.ok ? 'rgba(52,211,153,0.35)' : 'rgba(239,68,68,0.35)'}` }}>
            <div className="text-[12.5px] flex-1" style={{ color: flash.ok ? '#A7F3D0' : '#FCA5A5' }}>{flash.text}</div>
            <button className="text-slate-400 hover:text-slate-200" onClick={() => setFlash(null)}><X size={14} /></button>
          </div>
        )}

        {loading ? (
          <div className="se-panel p-12 text-center">
            <Loader2 size={26} className="animate-spin mx-auto mb-2" style={{ color: 'var(--se-violet-2)' }} />
            <div className="text-slate-400 text-[13px]">جاري التحميل...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="se-panel p-12 text-center text-slate-400">لا يوجد موردون مطابقون</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((s, i) => (
              <SupplierCard
                key={s.name || i}
                supplier={s}
                index={i}
                menuOpen={menuFor === s.name}
                onOpenMenu={() => setMenuFor(s.name)}
                onCloseMenu={() => setMenuFor(null)}
                onOpen={() => nav('/suppliers/' + encodeURIComponent(s.name))}
                onDeleteRequest={() => { setMenuFor(null); setPendingDelete(s); }}
              />
            ))}
          </div>
        )}

        {pendingDelete && (
          <DeleteSupplierModal
            supplier={pendingDelete}
            deleting={deleting}
            onCancel={() => !deleting && setPendingDelete(null)}
            onConfirm={confirmDelete}
          />
        )}
      </main>
    </div>
  );
}

/* ── Supplier card with individual ⋮ menu ─────────────────── */
function SupplierCard({ supplier: s, index: i, menuOpen, onOpenMenu, onCloseMenu, onOpen, onDeleteRequest }) {
  const menuRef = useRef(null);
  return (
    <div className="se-panel-hi p-5 relative transition-transform hover:-translate-y-1 cursor-pointer"
         data-testid={`supplier-card-${i}`}
         style={{ zIndex: menuOpen ? 30 : 1 }}
         onClick={onOpen}>
      {/* ⋮ menu button — stopPropagation so it never triggers the card click */}
      <div className="absolute top-3 left-3" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid={`supplier-menu-btn-${i}`}
          title="خيارات المورد"
          onClick={(e) => { e.stopPropagation(); menuOpen ? onCloseMenu() : onOpenMenu(); }}
          className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-white/10"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#C7C3DB' }}>
          <MoreVertical size={15} />
        </button>
        {menuOpen && (
          <div ref={menuRef}
               data-testid={`supplier-menu-${i}`}
               onClick={(e) => e.stopPropagation()}
               className="absolute mt-1 rounded-lg overflow-hidden"
               style={{ minWidth: 180, background: '#141220', border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.4)', zIndex: 40 }}>
            <button
              data-testid={`supplier-delete-${i}`}
              onClick={onDeleteRequest}
              className="w-full text-right px-3 py-2.5 flex items-center gap-2 text-[12.5px] font-semibold hover:bg-red-500/15"
              style={{ color: '#FCA5A5' }}>
              <Trash2 size={13} /> حذف المورد
            </button>
          </div>
        )}
      </div>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center"
             style={{ background: 'rgba(109,93,251,0.18)', border: '1px solid rgba(109,93,251,0.36)' }}>
          <Building size={20} style={{ color: '#B7ADFF' }} />
        </div>
        <div className="min-w-0 flex-1 pl-10" /* leaves room for the ⋮ button */>
          <div className="text-[14px] font-bold truncate" style={{ color: '#F1EEFB' }} title={s.name}>{s.name}</div>
          <div className="text-slate-400 text-[11px] mt-0.5">
            {numFmt(s.schools)} مدرسة · {numFmt(s.items)} صنف · {numFmt(s.depts || 0)} إدارة
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="text-[10px] text-slate-400">الكمية المخصصة</div>
          <div className="text-[15px] font-extrabold tabular-nums mt-0.5" style={{ color: '#9D8CFF' }}>{numFmt(s.allocated_qty)}</div>
          <div className="text-[9.5px] text-slate-500 mt-0.5">قطعة</div>
        </div>
        <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="text-[10px] text-slate-400">القيمة</div>
          <div className="text-[15px] font-extrabold tabular-nums mt-0.5" style={{ color: '#38D6C7' }}>{moneyFmt(s.allocated_value)}</div>
          <div className="text-[9.5px] text-slate-500 mt-0.5">ريال</div>
        </div>
      </div>
      {s.completion != null ? (
        <>
          <div className="se-progress mb-1"><div style={{ width: `${Math.min(100, s.completion)}%` }} /></div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-400">نسبة إنجاز التوريد</span>
            <span className="font-bold tabular-nums" style={{ color: s.completion >= 80 ? '#34D399' : s.completion >= 40 ? '#FBBF24' : '#F87171' }}>
              {pctFmt(s.completion)}
            </span>
          </div>
        </>
      ) : (
        <div className="text-slate-500 text-[10.5px]">نسبة إنجاز التوريد: غير متوفر</div>
      )}
    </div>
  );
}

/* ── Supplier-specific confirmation modal ─────────────────── */
function DeleteSupplierModal({ supplier, deleting, onCancel, onConfirm }) {
  return (
    <div
      data-testid="supplier-delete-modal"
      onClick={onCancel}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,9,16,0.72)', backdropFilter: 'blur(6px)' }}>
      <div onClick={(e) => e.stopPropagation()}
           className="se-panel-hi max-w-lg w-full"
           style={{ padding: 22 }}>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.4)' }}>
            <AlertTriangle size={18} style={{ color: '#F87171' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold" style={{ color: '#F1EEFB' }}>حذف بيانات المورد</div>
            <div className="text-[12.5px] mt-2 leading-relaxed" style={{ color: '#C7C3DB' }} data-testid="supplier-delete-modal-body">
              هل أنت متأكد من حذف بيانات <b style={{ color: '#F1EEFB' }} data-testid="supplier-delete-modal-name">«{supplier.name}»</b>؟
              <br />
              سيتم حذف جميع بيانات هذا المورد فقط ولن تتأثر بيانات الموردين الآخرين.
            </div>
            <div className="text-[11.5px] mt-3 text-slate-400">
              السجلات المتوقع حذفها: <b className="tabular-nums" style={{ color: '#F1EEFB' }}>{Number(supplier.records || 0).toLocaleString('en-US')}</b>
              <span className="mx-2">·</span>
              المدارس: <b className="tabular-nums" style={{ color: '#F1EEFB' }}>{Number(supplier.schools || 0).toLocaleString('en-US')}</b>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end pt-3 border-t border-white/10">
          <button
            data-testid="supplier-delete-cancel"
            disabled={deleting}
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-[12.5px] font-semibold disabled:opacity-40"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#F1EEFB' }}>
            إلغاء
          </button>
          <button
            data-testid="supplier-delete-confirm"
            disabled={deleting}
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-[12.5px] font-bold flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg,#EF4444,#DC2626)', color: '#fff' }}>
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            حذف المورد
          </button>
        </div>
      </div>
    </div>
  );
}
